import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiX, FiUser, FiMail, FiChevronDown, FiExternalLink } from 'react-icons/fi';
import { db, collection, getDocs, query, where, updateDoc, doc, addDoc, serverTimestamp } from '../../../components/firebase';
import { useTheme } from '../../../context/ThemeContext';
import { toast } from 'react-toastify';

const STATUS_OPTIONS = [
    { value: 'applied', label: 'Applied', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
    { value: 'interviewed', label: 'Interviewed', color: '#eab308', bg: 'rgba(234,179,8,0.12)' },
    { value: 'selected', label: 'Selected', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
    { value: 'rejected', label: 'Rejected', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
];

const ApplicantsModal = ({ show, onClose, job }) => {
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);
    const { isDarkMode } = useTheme();

    const fetchApplicants = useCallback(async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'applications'), where('jobId', '==', job.id));
            const snap = await getDocs(q);
            setApplicants(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error('Error fetching applicants:', err);
            toast.error('Failed to load applicants');
        } finally {
            setLoading(false);
        }
    }, [job]);

    useEffect(() => {
        if (show && job) fetchApplicants();
    }, [show, job, fetchApplicants]);

    const updateStatus = async (applicant, newStatus) => {
        setUpdatingId(applicant.id);
        try {
            await updateDoc(doc(db, 'applications', applicant.id), { status: newStatus });
            setApplicants(prev => prev.map(a => a.id === applicant.id ? { ...a, status: newStatus } : a));

            const statusLabel = STATUS_OPTIONS.find(s => s.value === newStatus)?.label || newStatus;
            await addDoc(collection(db, 'notifications'), {
                title: 'Application Update',
                message: `Your application for "${job.title}" has been updated to: ${statusLabel} `,
                type: newStatus === 'selected' ? 'success' : newStatus === 'rejected' ? 'warning' : 'info',
                recipient: 'specific',
                recipientUid: applicant.studentId,
                timestamp: serverTimestamp(),
                createdAt: serverTimestamp(),
                readBy: []
            });

            toast.success(`Status updated to ${statusLabel} `);
        } catch (err) {
            toast.error('Failed to update status');
        } finally {
            setUpdatingId(null);
        }
    };

    if (!show) return null;

    const statusCounts = STATUS_OPTIONS.reduce((acc, s) => {
        acc[s.value] = applicants.filter(a => a.status === s.value).length;
        return acc;
    }, {});

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(20px)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
        }} onClick={onClose}>
            <motion.div
                initial={{ scale: 0.92, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: '100%', maxWidth: 750, background: 'var(--bg-main)', borderRadius: 32,
                    border: '1px solid var(--border)', maxHeight: '90vh', overflowY: 'auto',
                    boxShadow: isDarkMode ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' : '0 25px 50px -12px rgba(0, 0, 0, 0.1)'
                }}
            >
                {/* Header */}
                <div style={{ padding: '28px 28px 20px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-main)', zIndex: 1, borderRadius: '32px 32px 0 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <h2 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: 800, color: 'var(--text-main)' }}>Applicants</h2>
                            <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>
                                {job?.title} — <span style={{ color: 'var(--accent)' }}>{applicants.length} applicant{applicants.length !== 1 ? 's' : ''}</span>
                            </p>
                        </div>
                        <button onClick={onClose}
                            style={{ width: 36, height: 36, borderRadius: 12, background: 'var(--bg-surface)', border: 'none', color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FiX size={18} />
                        </button>
                    </div>

                    {/* Status Overview Pills */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                        {STATUS_OPTIONS.map(s => (
                            <span key={s.value} style={{ padding: '4px 12px', borderRadius: 100, background: s.bg, color: s.color, fontSize: '11px', fontWeight: 700 }}>
                                {s.label}: {statusCounts[s.value] || 0}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Applicants List */}
                <div style={{ padding: '20px 28px 28px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Loading applicants...</div>
                    ) : applicants.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                            <FiUser size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                            <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-main)' }}>No applicants yet</p>
                            <p style={{ fontSize: '13px' }}>Applicants will appear here once students apply</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {applicants.map((applicant, i) => {
                                const currentStatus = STATUS_OPTIONS.find(s => s.value === applicant.status) || STATUS_OPTIONS[0];
                                return (
                                    <motion.div
                                        key={applicant.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.05 }}
                                        style={{
                                            padding: '18px 20px', background: 'var(--bg-card)', borderRadius: 20,
                                            border: '1px solid var(--border)',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            gap: 16, flexWrap: 'wrap'
                                        }}
                                    >
                                        {/* Applicant Info */}
                                        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flex: 1, minWidth: 200 }}>
                                            <div style={{
                                                width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                                                background: 'var(--accent-hover)', display: 'flex',
                                                alignItems: 'center', justifyContent: 'center',
                                                color: 'var(--accent)', fontWeight: 700, fontSize: '15px',
                                                overflow: 'hidden'
                                            }}>
                                                {applicant.studentPhoto ? (
                                                    <img src={applicant.studentPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    (applicant.studentName || applicant.studentEmail || '?').charAt(0).toUpperCase()
                                                )}
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {applicant.studentName || 'Student'}
                                                </div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, opacity: 0.8 }}>
                                                    <FiMail size={11} /> {applicant.studentEmail}
                                                </div>
                                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: 2, opacity: 0.6 }}>
                                                    Applied {applicant.appliedAt?.toDate ? applicant.appliedAt.toDate().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Recently'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* View Profile Button */}
                                        <button
                                            onClick={() => window.open(`/portfolio/${applicant.studentId}`, '_blank')}
                                            title="View Student Profile"
                                            style={{
                                                padding: '7px 14px', borderRadius: 10,
                                                background: 'transparent',
                                                border: '1px solid var(--border)',
                                                color: 'var(--text-muted)', fontSize: '12px',
                                                fontWeight: 700, cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', gap: 5,
                                                flexShrink: 0, transition: 'all 0.2s',
                                                whiteSpace: 'nowrap'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                                        >
                                            <FiExternalLink size={12} /> Profile
                                        </button>

                                        {/* Status Dropdown */}
                                        <div style={{ position: 'relative', flexShrink: 0 }}>
                                            <select
                                                value={applicant.status}
                                                onChange={(e) => updateStatus(applicant, e.target.value)}
                                                disabled={updatingId === applicant.id}
                                                style={{
                                                    padding: '8px 32px 8px 14px', borderRadius: 12,
                                                    background: currentStatus.bg, border: `1px solid ${currentStatus.color} 30`,
                                                    color: currentStatus.color, fontSize: '12px', fontWeight: 700,
                                                    cursor: 'pointer', outline: 'none', appearance: 'none',
                                                    opacity: updatingId === applicant.id ? 0.5 : 1
                                                }}
                                            >
                                                {STATUS_OPTIONS.map(s => (
                                                    <option key={s.value} value={s.value} style={{ background: 'var(--bg-card)', color: 'var(--text-main)' }}>{s.label}</option>
                                                ))}
                                            </select>
                                            <FiChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: currentStatus.color, pointerEvents: 'none' }} />
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default ApplicantsModal;
